export function stripHtml(html) {
  return String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function paragraphsToHtml(paragraphs) {
  if (!Array.isArray(paragraphs)) return String(paragraphs || '');
  return paragraphs.map((p) => String(p || '')).join('');
}

/** Detect FAQ pairs from H2/H3 question headings + following body copy. */
export function extractFaqCandidates(html) {
  if (!html) return [];
  const faqs = [];
  const headingRe = /<h([23])[^>]*>([\s\S]*?)<\/h\1>/gi;
  let match;
  while ((match = headingRe.exec(html)) !== null) {
    const headingText = stripHtml(match[2]).trim();
    if (!headingText.includes('?')) continue;
    const after = html.slice(match.index + match[0].length);
    const nextHead = after.search(/<h[23][\s>]/i);
    const chunk = nextHead === -1 ? after : after.slice(0, nextHead);
    const answer = stripHtml(chunk).trim();
    if (answer.length >= 20) {
      faqs.push({ question: headingText, answer });
    }
  }
  return faqs;
}

export function faqsFromArticleContent(content) {
  return extractFaqCandidates(paragraphsToHtml(content));
}

/** Detect FAQ pairs from author-inserted <details><summary>Q</summary>A</details> accordions. */
export function extractAccordionFaqs(html) {
  if (!html) return [];
  const faqs = [];
  const detailsRe = /<details\b[^>]*>([\s\S]*?)<\/details>/gi;
  let match;
  while ((match = detailsRe.exec(html)) !== null) {
    const inner = match[1];
    const summaryMatch = inner.match(/<summary\b[^>]*>([\s\S]*?)<\/summary>/i);
    if (!summaryMatch) continue;
    const question = stripHtml(summaryMatch[1]).trim();
    const answer = stripHtml(inner.replace(summaryMatch[0], '')).trim();
    if (question && answer.length >= 1) {
      faqs.push({ question, answer });
    }
  }
  return faqs;
}

export function accordionFaqsFromContent(content) {
  return extractAccordionFaqs(paragraphsToHtml(content));
}

/** All FAQ pairs on the page (heading-detected + authored accordions), de-duped by question. */
export function allFaqsFromContent(content) {
  const html = paragraphsToHtml(content);
  const combined = [...extractFaqCandidates(html), ...extractAccordionFaqs(html)];
  const seen = new Set();
  return combined.filter((faq) => {
    const key = faq.question.trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** Detect HowTo from "How to" heading + ordered list steps. */
export function extractHowToFromContent(content) {
  const html = paragraphsToHtml(content);
  if (!html) return null;

  const headingRe = /<h[23][^>]*>([\s\S]*?)<\/h[23]>/gi;
  let match;
  while ((match = headingRe.exec(html)) !== null) {
    const headingText = stripHtml(match[2]).trim();
    if (!/how to/i.test(headingText)) continue;

    const after = html.slice(match.index + match[0].length);
    const olMatch = after.match(/<ol[^>]*>([\s\S]*?)<\/ol>/i);
    if (!olMatch) continue;

    const steps = (olMatch[1].match(/<li[^>]*>([\s\S]*?)<\/li>/gi) || [])
      .map((li) => stripHtml(li))
      .filter((text) => text.length > 0);

    if (steps.length >= 3) {
      return { name: headingText, steps };
    }
  }

  return null;
}
